import { useCallback, useEffect, useRef, useState } from "react";

import { Avatar, Box, CircularProgress, Stack, Typography } from "@mui/material";

import type { LocalAudioTrack, LocalVideoTrack } from "twilio-video";

import { UserRole } from "@/types";

import useAITeachingAssistant from "../hooks/useAITeachingAssistant";
import useDeepgramTranscription from "../hooks/useDeepgramTranscription";
import useFullTranscript from "../hooks/useFullTranscript";
import useVideoContext from "../hooks/useVideoContext";

import AIAssistant from "./AIAssistant";
import VideoStream, { VideoSize } from "./VideoStream";
import Whiteboard from "./Whiteboard";

interface VideoAreaProps {
  roomToken: string;
  isWhiteboardOpen: boolean;
  userRole: UserRole;
  isTranscribing: boolean;
  isLessonCompleted: boolean;
  onToggleWhiteboard: () => void;
  userWantsToDisconnect?: boolean;
  isSandbox?: boolean;
}

function VideoArea({
  roomToken,
  isWhiteboardOpen,
  userRole,
  isTranscribing,
  isLessonCompleted,
  onToggleWhiteboard,
  userWantsToDisconnect = false,
  isSandbox = false,
}: VideoAreaProps) {
  const initialMicEnabled =
    sessionStorage.getItem("initialMicEnabled") === "true";
  const initialCameraEnabled =
    sessionStorage.getItem("initialCameraEnabled") === "true";
  const [initialized, setInitialized] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const {
    room,
    isConnecting: connecting,
    connect,
    onError,
    getAudioAndVideoTracks,
    localTracks,
    toggleAudio,
    toggleVideo,
    disconnectFromRoom,
  } = useVideoContext();
  const disconnectRef = useRef(disconnectFromRoom);
  const connectInFlightRef = useRef(false);

  // Note: When VITE_USE_MOCK_VIDEO=true, localTracks will use real devices
  // but still be wrapped in mock LocalTrack objects for testing

  const { transcripts } = useDeepgramTranscription(room, isTranscribing);

  // Full transcript for tutors only
  const localAudioTracks = localTracks.filter(
    (track): track is LocalAudioTrack => track.kind === "audio",
  );
  const { fullTranscript } = useFullTranscript(
    room,
    userRole,
    localAudioTracks,
  );

  // AI Teaching Assistant
  const { suggestions, isAnalyzing, dismissSuggestion } =
    useAITeachingAssistant(
      fullTranscript,
      userRole === UserRole.Tutor, // Only enable for tutors
    );

  // Derive a key for local video stream from its track id so it remounts on new track
  const localVideoTrack = localTracks.find(
    (t): t is LocalVideoTrack => t.kind === "video",
  );
  const localVideoKey = localVideoTrack?.mediaStreamTrack.id;

  // Force re-render on participant connect/disconnect
  const [, setCount] = useState(0);
  useEffect(() => {
    if (!room) return;
    const handler = () => setCount((c) => c + 1);
    room.on("participantConnected", handler);
    room.on("participantDisconnected", handler);
    return () => {
      room.off("participantConnected", handler);
      room.off("participantDisconnected", handler);
    };
  }, [room]);

  // Participants
  const localParticipant = room?.localParticipant;
  const remoteParticipants = room ? Array.from(room.participants.values()) : [];
  const remoteParticipant = remoteParticipants[0];

  // Check if remote participant has both screen and camera tracks
  const [hasScreenAndCamera, setHasScreenAndCamera] = useState(false);
  // Track which view is active: "screen" or "camera" (default to "screen" when screen sharing)
  const [activeView, setActiveView] = useState<"screen" | "camera">("screen");

  const toggleView = useCallback(() => {
    setActiveView((prev) => (prev === "screen" ? "camera" : "screen"));
  }, []);

  useEffect(() => {
    if (!remoteParticipant) {
      setHasScreenAndCamera(false);
      setActiveView("screen");
      return;
    }

    const checkTracks = () => {
      const tracks = Array.from(remoteParticipant.tracks.values());
      const hasScreenTrack = tracks.some(
        (pub) => pub.track?.kind === "video" && pub.track.name === "screen",
      );
      const hasCameraTrack = tracks.some(
        (pub) =>
          pub.track?.kind === "video" &&
          pub.track.name !== "screen" &&
          pub.track.name !== "whiteboard",
      );
      const bothPresent = hasScreenTrack && hasCameraTrack;
      setHasScreenAndCamera(bothPresent);
      // Reset to screen view if screen sharing stops
      if (!hasScreenTrack) {
        setActiveView("screen");
      }
    };

    // Check initially
    checkTracks();

    // Listen for track changes
    const handleTrackPublished = () => checkTracks();
    const handleTrackUnpublished = () => checkTracks();
    const handleTrackSubscribed = () => checkTracks();
    const handleTrackUnsubscribed = () => checkTracks();

    remoteParticipant.on("trackPublished", handleTrackPublished);
    remoteParticipant.on("trackUnpublished", handleTrackUnpublished);
    remoteParticipant.on("trackSubscribed", handleTrackSubscribed);
    remoteParticipant.on("trackUnsubscribed", handleTrackUnsubscribed);

    return () => {
      remoteParticipant.off("trackPublished", handleTrackPublished);
      remoteParticipant.off("trackUnpublished", handleTrackUnpublished);
      remoteParticipant.off("trackSubscribed", handleTrackSubscribed);
      remoteParticipant.off("trackUnsubscribed", handleTrackUnsubscribed);
    };
  }, [remoteParticipant]);

  useEffect(() => {
    disconnectRef.current = disconnectFromRoom;
  }, [disconnectFromRoom]);

  const connectToRoom = useCallback(async () => {
    if (connectInFlightRef.current) {
      return;
    }

    connectInFlightRef.current = true;
    try {
      // Acquire local tracks before connecting
      const tracks = await getAudioAndVideoTracks();
      const tracksForConnect =
        tracks.length > 0
          ? (tracks as (LocalAudioTrack | LocalVideoTrack)[])
          : undefined;
      await connect(roomToken, tracksForConnect);
    } catch (e: unknown) {
      setError(e as Error);
      onError(e as Error);
    } finally {
      connectInFlightRef.current = false;
    }
  }, [connect, getAudioAndVideoTracks, onError, roomToken]);

  useEffect(() => {
    if (
      roomToken &&
      !room &&
      !connecting &&
      !isLessonCompleted &&
      !userWantsToDisconnect &&
      !isSandbox
    ) {
      connectToRoom();
    }
  }, [
    connectToRoom,
    connecting,
    isLessonCompleted,
    room,
    roomToken,
    userWantsToDisconnect,
    isSandbox,
  ]);

  useEffect(
    () => () => {
      disconnectRef.current();
    },
    [],
  );

  // Apply initial mute states after room connection
  useEffect(() => {
    if (room && localTracks.length > 0 && !initialized) {
      const audioTrack = localTracks.find((track) => track.kind === "audio");
      const videoTrack = localTracks.find((track) => track.kind === "video");

      // Apply initial mute states
      if (audioTrack && !initialMicEnabled) {
        toggleAudio(); // This will unpublish the audio track
      }

      if (videoTrack && !initialCameraEnabled) {
        toggleVideo(); // This will unpublish the video track
      }
      setInitialized(true);
    }
  }, [
    room,
    localTracks,
    initialMicEnabled,
    initialCameraEnabled,
    toggleAudio,
    toggleVideo,
    initialized,
  ]);

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        bgcolor: "#0A211F",
        borderRadius: 3,
        overflow: "hidden",
      }}
    >
      {/* Sandbox Placeholders were moved into the main content area */}

      {!room && !connecting && !isSandbox && (
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={2}
          sx={{
            position: "fixed",
            inset: 0,
            zIndex: 50,
            backgroundColor: "rgba(0,0,0,.5)",
          }}
        >
          <CircularProgress sx={{ color: "common.white" }} />
          <Typography variant="subtitle2" sx={{ color: "common.white" }}>
            Initializing connection...
          </Typography>
          {error && (
            <Typography variant="body2" sx={{ color: "error.light" }}>
              Error: {error.message}
            </Typography>
          )}
        </Stack>
      )}
      {connecting && (
        <Stack
          alignItems="center"
          justifyContent="center"
          spacing={2}
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 20,
            bgcolor: "rgba(28, 72, 46, 0.75)",
            color: "common.white",
          }}
        >
          <CircularProgress sx={{ color: "common.white" }} />
          <Typography variant="subtitle2">Connecting...</Typography>
        </Stack>
      )}
      {(room || isSandbox) && (
        <>
          {/* Main area: show remote participant if present */}
          {isWhiteboardOpen ? (
            <>
              <Whiteboard userRole={userRole} />
              {remoteParticipant ? (
                <VideoStream
                  participant={remoteParticipant}
                  videoSize={VideoSize.SMALL}
                  onToggleWhiteboard={onToggleWhiteboard}
                  isWhiteboardActive={true}
                />
              ) : isSandbox ? (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 16,
                    left: 16,
                    width: 240,
                    height: 135,
                    bgcolor: "rgba(10, 33, 31, 0.9)",
                    borderRadius: 2,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    backdropFilter: "blur(10px)",
                    zIndex: 10,
                    boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.37)",
                    cursor: "pointer",
                  }}
                  onClick={onToggleWhiteboard}
                >
                  <Avatar
                    src={userRole === UserRole.Tutor ? "https://api.dicebear.com/7.x/avataaars/svg?seed=AlexJohnson" : "https://api.dicebear.com/7.x/avataaars/svg?seed=DanaCalder"}
                    sx={{
                      width: 48,
                      height: 48,
                      mb: 0.5,
                      border: "2px solid rgba(255,255,255,0.2)"
                    }}
                  />
                  <Typography
                    sx={{
                      color: "rgba(255, 255, 255, 0.9)",
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      lineHeight: 1.2,
                    }}
                  >
                    {userRole === UserRole.Tutor ? "Alex Johnson" : "Dana Calder"}
                  </Typography>
                  <Typography
                    sx={{
                      color: "rgba(255, 255, 255, 0.5)",
                      fontSize: "0.65rem",
                      fontWeight: 500,
                    }}
                  >
                    {userRole === UserRole.Tutor ? "Student" : "Tutor"}
                  </Typography>
                </Box>
              ) : null}
            </>
          ) : (
            <>
              {remoteParticipant ? (
                <>
                  {/* Main view: show screen or camera based on activeView */}
                  <VideoStream
                    participant={remoteParticipant}
                    preferredTrackType={
                      hasScreenAndCamera ? activeView : undefined
                    }
                  />
                  {/* Show toggle PIP at top right when remote participant is sharing screen and has camera */}
                  {hasScreenAndCamera && (
                    <VideoStream
                      participant={remoteParticipant}
                      videoSize={VideoSize.SMALL}
                      preferredTrackType={
                        activeView === "screen" ? "camera" : "screen"
                      }
                      pipPosition="top-right"
                      draggable={true}
                      onClick={toggleView}
                    />
                  )}
                </>
              ) : isSandbox ? (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    p: 2,
                    zIndex: 5,
                    display: "flex",
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      background: "radial-gradient(circle at center, #1C482E 0%, #0A211F 100%)",
                      borderRadius: 2,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px solid rgba(255, 255, 255, 0.05)",
                    }}
                  >
                    <Avatar
                      src={userRole === UserRole.Tutor ? "https://api.dicebear.com/7.x/avataaars/svg?seed=AlexJohnson" : "https://api.dicebear.com/7.x/avataaars/svg?seed=DanaCalder"}
                      sx={{
                        width: 120,
                        height: 120,
                        mb: 2,
                        boxShadow: "0 0 40px rgba(0,0,0,0.5)",
                        border: "4px solid rgba(255,255,255,0.1)"
                      }}
                    />
                    <Typography sx={{
                      color: "rgba(255, 255, 255, 0.9)",
                      variant: "h5",
                      fontWeight: 600
                    }}>
                      {userRole === UserRole.Tutor ? "Alex Johnson" : "Dana Calder"}
                    </Typography>
                    <Typography sx={{
                      color: "rgba(255, 255, 255, 0.5)",
                      variant: "body2",
                      mt: 0.5
                    }}>
                      {userRole === UserRole.Tutor ? "Student" : "Tutor"}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    position: "relative",
                    flexGrow: 1,
                    width: "100%",
                    height: "100%",
                    overflow: "hidden",
                    px: 3,
                    textAlign: "center",
                  }}
                >
                  <Box>
                    <Typography
                      variant="text20B"
                      sx={{
                        mb: 1,
                        color: "common.white",
                      }}
                    >
                      Waiting for {userRole === "tutor" ? "student" : "tutor"}{" "}
                      to connect...
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255, 255, 255, 0.6)" }}
                    >
                      Please wait while the other participant joins the lesson
                    </Typography>
                  </Box>
                </Stack>
              )}
              {localParticipant ? (
                <VideoStream
                  key={localVideoKey}
                  participant={localParticipant}
                  isLocal
                />
              ) : isSandbox ? (
                <Box
                  sx={{
                    width: 240,
                    height: 180,
                    position: "absolute",
                    bottom: 16,
                    right: 16,
                    bgcolor: "rgba(255, 255, 255, 0.1)",
                    borderRadius: 2,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    backdropFilter: "blur(12px)",
                    boxShadow: "0 8px 32px 0 rgba(0, 0, 0, 0.4)",
                    zIndex: 10,
                  }}
                >
                  <Avatar
                    src={userRole === UserRole.Tutor ? "https://api.dicebear.com/7.x/avataaars/svg?seed=DanaCalder" : "https://api.dicebear.com/7.x/avataaars/svg?seed=AlexJohnson"}
                    sx={{
                      width: 64,
                      height: 64,
                      mb: 1,
                      border: "2px solid rgba(255,255,255,0.2)"
                    }}
                  />
                  <Typography
                    sx={{
                      color: "rgba(255, 255, 255, 0.9)",
                      fontSize: "0.85rem",
                      fontWeight: 600,
                      lineHeight: 1.2,
                    }}
                  >
                    {userRole === UserRole.Tutor ? "Dana Calder" : "Alex Johnson"}
                  </Typography>
                  <Typography
                    sx={{
                      color: "rgba(255, 255, 255, 0.5)",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                    }}
                  >
                    {userRole === UserRole.Tutor ? "Tutor" : "Student"}
                  </Typography>
                </Box>
              ) : null}
            </>
          )}
          {isTranscribing && (
            <Box
              sx={{
                position: "absolute",
                bottom: 24,
                left: "50%",
                transform: "translateX(-50%)",
                width: {
                  xs: "calc(100% - 32px)",
                  sm: 384,
                },
                borderRadius: 2,
                px: 1.5,
                py: 1.25,
                bgcolor: "rgba(0, 0, 0, 0.5)",
                backdropFilter: "blur(8px)",
                boxShadow: 8,
                zIndex: 50,
              }}
            >
              {transcripts.length > 0 ? (
                <Typography variant="body1" sx={{ color: "common.white" }}>
                  {transcripts[transcripts.length - 1]}
                </Typography>
              ) : (
                <Typography
                  variant="body1"
                  sx={{ color: "rgba(255, 255, 255, 0.6)" }}
                >
                  Listening...
                </Typography>
              )}
            </Box>
          )}
          {/* AI Teaching Assistant */}
          <AIAssistant
            suggestions={suggestions}
            isAnalyzing={isAnalyzing}
            onDismiss={dismissSuggestion}
          />
        </>
      )}
    </Box>
  );
}

export default VideoArea;
